import { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getOrder, updateOrderStatus, type OrderDetail } from '../../shared/api/orders.js'
import { ApiError } from '../../shared/types/api.js'
import { canManageOrders } from '../../shared/utils/permissions.js'
import { availableOrderActions, ORDER_TYPES, type OrderStatus } from '../../shared/utils/orders.js'
import { ROUTES } from '../../app/routes.js'
import OrderStatusBadge from './components/OrderStatusBadge.js'
import OrderActionButtons from './components/OrderActionButtons.js'

type DetailStatus = 'loading' | 'ready' | 'error' | 'notFound'

type OrderTypeValue = typeof ORDER_TYPES[number]

export default function OrderDetailPage() {
  const { t } = useTranslation()
  const { orderId } = useParams<{ orderId: string }>()

  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const role = useOrgStore(s => s.activeOrganization)?.role
  const canManage = canManageOrders(role)

  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [status, setStatus] = useState<DetailStatus>('loading')
  const [pending, setPending] = useState(false)
  const [actionError, setActionError] = useState('')

  const load = useCallback(async () => {
    if (!orgId || !orderId) return
    setStatus('loading')
    setActionError('')
    try {
      const data = await getOrder(orgId, orderId)
      setOrder(data)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 404 ? 'notFound' : 'error')
    }
  }, [orgId, orderId])

  useEffect(() => {
    void load()
  }, [load])

  async function handleAction(nextStatus: OrderStatus) {
    if (!orgId || !order) return
    if (nextStatus === 'REJECTED' || nextStatus === 'CANCELLED') {
      const ok = window.confirm(t('orders.confirmDestructive', { status: t(`orderStatus.${nextStatus}`) }))
      if (!ok) return
    }
    setPending(true)
    setActionError('')
    try {
      await updateOrderStatus(orgId, order.id, { status: nextStatus })
      setActionError('')
      await load()
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        await load()
        setActionError(t('orders.concurrencyError'))
      } else {
        setActionError(t('orders.statusUpdateError'))
      }
    } finally {
      setPending(false)
    }
  }

  if (!orgId) return null

  if (status === 'loading' && !order) {
    return (
      <section className="page-section">
        <h2>{t('app.orders')}</h2>
        <div className="page-loading"><div className="spinner" /></div>
      </section>
    )
  }

  if (status === 'notFound') {
    return (
      <section className="page-section">
        <h2>{t('orders.notFound')}</h2>
        <Link className="btn btn-ghost" to={ROUTES.ORDERS}>{t('orders.backToList')}</Link>
      </section>
    )
  }

  if (status === 'error' || !order) {
    return (
      <section className="page-section">
        <h2>{t('app.orders')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('orders.loadErrorDetail')}</div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>{t('error.retry')}</button>
        </div>
      </section>
    )
  }

  const typeKey = `orderType.${(order.type as OrderTypeValue).toLowerCase()}`
  const actions = availableOrderActions(order.status)

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <Link className="btn btn-ghost" to={ROUTES.ORDERS}>← {t('orders.backToList')}</Link>
          <h2>{t('orders.detailTitle', { number: order.orderNumber })}</h2>
        </div>
        <div className="order-detail-status"><OrderStatusBadge status={order.status} /></div>
      </div>

      {actionError && <div className="form-error-banner">{actionError}</div>}

      <div className="order-detail-card">
        <div className="order-detail-row"><span>{t('orders.createdAt')}</span><strong>{new Date(order.createdAt).toLocaleString()}</strong></div>
        <div className="order-detail-row"><span>{t('orders.type')}</span><strong>{t(typeKey)}</strong></div>
        <div className="order-detail-row"><span>{t('orders.source')}</span><strong>{order.source}</strong></div>
        <div className="order-detail-row"><span>{t('orders.location')}</span><strong>{order.location ? `${order.location.name}${order.location.address ? `, ${order.location.address}` : ''}` : '—'}</strong></div>
      </div>

      {canManage && (
        <div className="order-detail-actions">
          <OrderActionButtons actions={actions} pending={pending} onAction={status => void handleAction(status)} />
        </div>
      )}

      <h3 className="order-detail-section-title">{t('orders.customerSection')}</h3>
      <div className="order-detail-card">
        <div className="order-detail-row"><span>{t('orders.guestName')}</span><strong>{order.guestName || '—'}</strong></div>
        <div className="order-detail-row"><span>{t('orders.guestPhone')}</span><strong>{order.guestPhone || '—'}</strong></div>
        <div className="order-detail-row"><span>{t('orders.guestEmail')}</span><strong>{order.guestEmail || '—'}</strong></div>
      </div>

      {order.type === 'DELIVERY' && (
        <>
          <h3 className="order-detail-section-title">{t('orders.deliverySection')}</h3>
          <div className="order-detail-card">
            <div className="order-detail-row"><span>{t('orders.deliveryAddress')}</span><strong>{order.deliveryAddress || '—'}</strong></div>
            {order.deliveryCoords && (
              <div className="order-detail-row">
                <span>{t('orders.deliveryCoords')}</span>
                <strong>{order.deliveryCoords.lat}, {order.deliveryCoords.lng}</strong>
              </div>
            )}
          </div>
        </>
      )}

      {order.comment ? (
        <>
          <h3 className="order-detail-section-title">{t('orders.comment')}</h3>
          <div className="order-detail-card"><p className="order-detail-comment">{order.comment}</p></div>
        </>
      ) : null}

      <h3 className="order-detail-section-title">{t('orders.itemsSection')}</h3>
      <ul className="order-items">
        {order.items.map(item => (
          <li key={item.id} className="order-item">
            <div className="order-item-info">
              <p className="order-item-name">
                {item.productName}
                {item.variantName && <span className="order-item-variant">{item.variantName}</span>}
              </p>
              {item.modifiers.length > 0 && (
                <ul className="order-item-mods">
                  {item.modifiers.map((m, i) => (
                    <li key={i}>{m.name} × {m.quantity} (+{m.price} {order.currency})</li>
                  ))}
                </ul>
              )}
            </div>
            <span className="order-item-price">{item.quantity} × {item.unitPrice} = {item.totalPrice} {order.currency}</span>
          </li>
        ))}
      </ul>

      <h3 className="order-detail-section-title">{t('orders.totalsSection')}</h3>
      <div className="order-detail-card">
        <div className="order-detail-row"><span>{t('orders.itemsTotal')}</span><strong>{order.itemsTotal} {order.currency}</strong></div>
        <div className="order-detail-row"><span>{t('orders.deliveryFee')}</span><strong>{order.deliveryFee} {order.currency}</strong></div>
        <div className="order-detail-row"><span>{t('orders.discountTotal')}</span><strong>{order.discountTotal} {order.currency}</strong></div>
        <div className="order-detail-row order-detail-total"><span>{t('orders.total')}</span><strong>{order.total} {order.currency}</strong></div>
      </div>

      <h3 className="order-detail-section-title">{t('orders.statusHistory')}</h3>
      <ul className="order-status-history">
        {order.statusLog.map((entry, i) => (
          <li key={i} className="order-status-history-item">
            <span className="order-status-history-time">{new Date(entry.createdAt).toLocaleString()}</span>
            <span className="order-status-history-transition">
              {entry.fromStatus ? t(`orderStatus.${entry.fromStatus}`) : '—'} → {t(`orderStatus.${entry.toStatus}`)}
            </span>
            <span className="order-status-history-reason">{entry.reason || ''}</span>
            {entry.changedBy && <span className="order-status-history-actor">{t('orders.actor', { id: entry.changedBy })}</span>}
          </li>
        ))}
      </ul>
    </section>
  )
}